import { bool, type InferType, object, string } from "yup";
import { isUSA } from "../../../../../common/utils";
import { getNameFromCountry } from "../../../mappers";
import { BankAddressSchemaGenerator } from "../../BankValidators/constants";
import { routingNumberBaseSchema } from "../../BankValidators/RoutingNumberInput/constants";
import { swiftCodeBaseSchema } from "../../BankValidators/SwiftCodeInput/constants";
import { validateBankCountryMatch, validateIntermediaryBankCountryMatch } from "./utils";

export enum BankDetailValidationError {
  CountryMismatch = "The country you selected doesn't match the country associated with the provided bank details. Please verify your country selection or check your bank information.",
}

export enum BankDetailsInputFields {
  BankCountry = "bankCountry",
  /**
   * Local schema field that will be loaded with validated data from MT.
   * Once loaded should contain a country name.
   */
  BankCountryValidatedCountry = "bankCountryValidatedCountry",
  BankName = "bankName",
  BankAddress = "bankAddress",
  RoutingNumber = "routingNumber",
  SwiftCode = "swiftCode",
  InternationalRoutingNumber = "internationalRoutingNumber",
  UsesIntermediaryBank = "usesIntermediaryBank",
  IntermediaryBankCountry = "intermediaryBankCountry",
  /**
   * Local schema field that will be loaded with validated data from MT.
   * Once loaded should contain a country name.
   */
  IntermediaryBankCountryValidatedCountry = "intermediaryBankCountryValidatedCountry",
  IntermediaryBankName = "intermediaryBankName",
  IntermediaryBankAddress = "intermediaryBankAddress",
  IntermediaryRoutingNumber = "intermediaryRoutingNumber",
  IntermediarySwiftCode = "intermediarySwiftCode",
  IntermediaryInternationalRoutingNumber = "intermediaryInternationalRoutingNumber",
}

const BankDetailsInputSchemaGenerator = ({
  testIsUSA,
}: {
  testIsUSA: (country: string | null | undefined) => boolean;
}) =>
  object().shape({
    [BankDetailsInputFields.BankCountry]: string() // has the format id:name
      .required("Required field")
      .test(
        "bank-country-validation",
        BankDetailValidationError.CountryMismatch,
        (value, context) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { bankCountryValidatedCountry } = context.parent;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          return validateBankCountryMatch(value, bankCountryValidatedCountry);
        },
      ),
    /**
     * Local schema field that will be loaded with validated data from MT.
     * Once loaded should contain a country name.
     */
    [BankDetailsInputFields.BankCountryValidatedCountry]: string().optional(),
    [BankDetailsInputFields.BankName]: string()
      .required("Required field")
      .matches(/\S/, "Bank name cannot be just spaces."),
    [BankDetailsInputFields.BankAddress]: BankAddressSchemaGenerator({
      countryFieldPath: BankDetailsInputFields.BankCountry,
    }),
    [BankDetailsInputFields.RoutingNumber]: string().when(BankDetailsInputFields.BankCountry, {
      is: (bankCountry: string) => testIsUSA(bankCountry),
      then: () => routingNumberBaseSchema.required("Required field"),
      otherwise: (schema) => schema,
    }),
    [BankDetailsInputFields.SwiftCode]: string().when(BankDetailsInputFields.BankCountry, {
      is: (bankCountry: string) => !testIsUSA(bankCountry),
      then: () => swiftCodeBaseSchema.required("Required field"),
      otherwise: (schema) => schema,
    }),
    [BankDetailsInputFields.InternationalRoutingNumber]: string(),
    [BankDetailsInputFields.UsesIntermediaryBank]: bool(),
    [BankDetailsInputFields.IntermediaryBankCountry]: string()
      .when(BankDetailsInputFields.UsesIntermediaryBank, {
        is: true,
        then: (schema) => schema.required("Required field"),
      })
      .test(
        "intermediary-bank-country-validation",
        BankDetailValidationError.CountryMismatch,
        (value, context) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { intermediaryBankCountryValidatedCountry, usesIntermediaryBank } =
            context.parent;

          return validateIntermediaryBankCountryMatch(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            usesIntermediaryBank,
            value,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            intermediaryBankCountryValidatedCountry,
          );
        },
      ),
    /**
     * Local schema field that will be loaded with validated data from MT.
     * Once loaded should contain a country name.
     */
    [BankDetailsInputFields.IntermediaryBankCountryValidatedCountry]: string().optional(),
    [BankDetailsInputFields.IntermediaryBankName]: string().when(
      BankDetailsInputFields.UsesIntermediaryBank,
      {
        is: true,
        then: (schema) =>
          schema.required("Required field").matches(/\S/, "Bank name cannot be just spaces."),
      },
    ),
    [BankDetailsInputFields.IntermediaryBankAddress]: BankAddressSchemaGenerator({
      countryFieldPath: BankDetailsInputFields.IntermediaryBankCountry,
    }),
    [BankDetailsInputFields.IntermediaryRoutingNumber]: string().when(
      [
        BankDetailsInputFields.UsesIntermediaryBank,
        BankDetailsInputFields.IntermediaryBankCountry,
      ],
      {
        is: (usesIntermediaryBank: boolean, bankCountry: string) =>
          usesIntermediaryBank && testIsUSA(bankCountry),
        then: () => routingNumberBaseSchema.required("Required field"),
        otherwise: (schema) => schema,
      },
    ),
    [BankDetailsInputFields.IntermediarySwiftCode]: string().when(
      [
        BankDetailsInputFields.UsesIntermediaryBank,
        BankDetailsInputFields.IntermediaryBankCountry,
      ],
      {
        is: (usesIntermediaryBank: boolean, bankCountry: string) =>
          usesIntermediaryBank && !testIsUSA(bankCountry),
        then: () => swiftCodeBaseSchema.required("Required field"),
        otherwise: (schema) => schema,
      },
    ),
    [BankDetailsInputFields.IntermediaryInternationalRoutingNumber]: string(),
  });

export const BankDetailsInputSchema = BankDetailsInputSchemaGenerator({
  testIsUSA: (country) => isUSA(getNameFromCountry(country)),
});

export type BankDetailsInputType = InferType<typeof BankDetailsInputSchema>;
